import { useMemo, useState } from 'react';

interface EnrouteViewProps {
  alarm: any;
  unitName?: string | null;
  unitStatus: string;
  onStatusChange: (status: string) => void;
  onComplete: () => void;
}

const STATUS_OPTIONS = [
  { code: '01', label: 'frei Wache', backendStatus: '01', endsMission: false },
  { code: '02', label: 'zum Berufungsort', backendStatus: '02', endsMission: false },
  { code: '03', label: 'am Berufungsort', backendStatus: '03', endsMission: false },
  { code: '04', label: 'zum Zielort', backendStatus: '04', endsMission: false },
  { code: '05', label: 'am Zielort', backendStatus: '05', endsMission: false },
  { code: '06', label: 'frei Funk', backendStatus: '06', endsMission: true },
  { code: '0', label: 'abgemeldet', backendStatus: '0', endsMission: false },
];

function mapUnitStatusToCode(status: string): string {
  if (STATUS_OPTIONS.some((option) => option.code === status)) return status;
  if (status === 'onScene') return '03';
  if (status === 'available') return '01';
  if (status === 'standby') return '0';
  return '02';
}

function isDisplayResource(value: string): boolean {
  const normalized = value.trim();
  if (!normalized) return false;
  // URLs and join/alarm links
  if (/^https?:\/\//i.test(normalized)) return false;
  if (normalized.includes('/join/')) return false;
  if (normalized.includes('/alarm/')) return false;
  // UUID (e.g. 550e8400-e29b-41d4-a716-446655440000)
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized)) return false;
  // MPDS / einsatz codes like 06A01, 99B02, 12C03
  if (/^\d+[A-Z]\d+/i.test(normalized)) return false;
  // Short codes like 6A, 99B
  if (/^[0-9]+[A-Z]$/i.test(normalized)) return false;
  // Single isolated letter (MPDS determinant like "A", "D")
  if (/^[A-Z]$/i.test(normalized)) return false;
  // Pure number
  if (/^\d+$/.test(normalized)) return false;
  return true;
}

export default function EnrouteView({ alarm, unitName, unitStatus, onStatusChange, onComplete }: EnrouteViewProps) {
  if (!alarm) {
    return <div>Loading...</div>;
  }

  const [showStatusPad, setShowStatusPad] = useState(false);
  const alarmCode = String(alarm.einsatzcode || '').trim();
  const alarmLabel = String(alarm.einsatzcodeLabel || '').trim();
  const alarmCodeSizeClass =
    alarmCode.length > 26
      ? 'text-2xl sm:text-3xl'
      : alarmCode.length > 18
        ? 'text-3xl sm:text-4xl'
        : 'text-4xl sm:text-5xl';

  const fullAlarmCode = useMemo(() => {
    const label = alarmLabel;
    const code = alarmCode;

    if (!label) return code || '-';
    if (label.toLowerCase().startsWith(code.toLowerCase())) return label;
    return code ? `${code} - ${label}` : label;
  }, [alarmCode, alarmLabel]);

  const alarmText = alarm.alarmText || alarm.sondertext || '-';
  const funkgruppe = alarm.funkgruppe || '-';
  const einsatzort = alarm?.location?.address || '-';
  const resources = (Array.isArray(alarm.additionalResources) ? alarm.additionalResources : [])
    .map((value: unknown) => String(value || '').trim())
    .filter((value: string) => value.length > 0)
    .filter(isDisplayResource);
  const assignedResources = (Array.isArray(alarm.assignedResources) ? alarm.assignedResources : [])
    .map((entry: any) => {
      if (!entry || typeof entry !== 'object') return null;
      const name = String(entry.name || entry.label || entry.id || '').trim();
      if (!name || !isDisplayResource(name)) return null;
      return {
        name,
        isAdditional: Boolean(entry.isAdditional),
      };
    })
    .filter((entry: any) => Boolean(entry));
  const dispatchedUnits = (Array.isArray(alarm.dispatchedUnits) ? alarm.dispatchedUnits : [])
    .map((value: unknown) => String(value || '').trim())
    .filter(isDisplayResource);
  // Always show the receiving unit's own name first.
  const ownName = unitName && unitName.trim() ? unitName.trim() : null;
  const displayedResources = (() => {
    const seen = new Set<string>();
    const combined: Array<{ name: string; isAdditional: boolean }> = [];

    if (ownName) {
      const key = ownName.toLowerCase();
      seen.add(key);
      combined.push({ name: ownName, isAdditional: false });
    }

    for (const unit of dispatchedUnits) {
      const key = unit.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      combined.push({ name: unit, isAdditional: false });
    }

    if (assignedResources.length > 0) {
      for (const resource of assignedResources) {
        const key = resource.name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        combined.push(resource);
      }
    } else {
      for (const resource of resources) {
        const key = resource.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        combined.push({ name: resource, isAdditional: false });
      }
    }

    return combined;
  })();

  const selectedStatusCode = mapUnitStatusToCode(unitStatus);
  const selectedStatusOption = STATUS_OPTIONS.find((option) => option.code === selectedStatusCode);

  const handleStatusSelection = (statusCode: string) => {
    const selected = STATUS_OPTIONS.find((option) => option.code === statusCode);
    if (!selected) return;

    onStatusChange(selected.backendStatus);
    setShowStatusPad(false);

    if (selected.code === '06' || selected.endsMission) {
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-dispatch-dark safe-screen flex flex-col p-4 pb-32 space-y-4 relative overflow-y-auto">

      {showStatusPad && (
        <div className="fixed inset-0 z-10 bg-black bg-opacity-50" onClick={() => setShowStatusPad(false)}>
          <div
            className="absolute inset-x-4 bottom-24 mx-auto w-auto max-w-xl bg-gray-900 border border-gray-700 rounded-2xl p-3 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="text-sm font-bold text-white mb-2">Status auswaehlen</p>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.code}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleStatusSelection(option.code);
                  }}
                  className={`text-left px-3 py-3 rounded-md border ${selectedStatusCode === option.code ? 'bg-orange-600 border-orange-400 text-white' : 'bg-gray-800 border-gray-700 text-gray-100'}`}
                >
                  <p className="text-xl font-bold leading-none">{option.code}</p>
                  <p className="text-xs mt-1 opacity-90">{option.label}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-900 border border-gray-700 p-5 rounded-lg space-y-5">
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wide text-gray-400">Einsatzcode</p>
          <div className="rounded-2xl border border-gray-700 bg-gray-950/70 px-4 py-4">
            <h2 className={`${alarmCodeSizeClass} leading-tight font-extrabold text-white break-words [overflow-wrap:anywhere]`}>
              {alarmCode || fullAlarmCode || '-'}
            </h2>
            {alarmLabel && !alarmLabel.toLowerCase().startsWith(alarmCode.toLowerCase()) && (
              <p className="mt-3 text-lg sm:text-xl font-medium leading-snug text-gray-100 [overflow-wrap:anywhere]">
                {alarmLabel}
              </p>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Alarmtext / Einsatztext</p>
          <p className="text-lg text-white">{alarmText}</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Funkgruppe</p>
          <p className="text-xl font-semibold text-white">{funkgruppe}</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Adresse / Einsatzort</p>
          <p className="text-xl font-semibold text-white">{einsatzort}</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Ressourcen</p>
          {displayedResources.length > 0 ? (
            <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {displayedResources.map((resource, index: number) => (
                <li key={`${resource.name}-${index}`} className="text-lg text-white">
                  • {resource.name}
                  {resource.isAdditional && (
                    <span className="ml-2 text-xs font-semibold uppercase text-yellow-300">nachalarmiert</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-lg text-gray-300">Keine zusaetzlichen Ressourcen</p>
          )}
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-700 p-4 rounded-lg">
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Aktueller Status</p>
        <p className="text-2xl font-bold text-white">
          {selectedStatusOption ? `${selectedStatusOption.code} - ${selectedStatusOption.label}` : unitStatus}
        </p>
      </div>

      <div className="safe-bottom-bar fixed inset-x-0 bottom-0 z-20 border-t border-gray-800 bg-gray-950/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto w-full max-w-4xl">
          <button
            onClick={() => setShowStatusPad((prev) => !prev)}
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-700 text-white font-bold px-4 py-3 rounded-lg text-sm shadow-lg"
          >
            Status
          </button>
        </div>
      </div>
    </div>
  );
}


